import React, { Fragment, useEffect, useState } from "react";
import Breadcrumb from "../common/breadcrumb";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Table,
  Label,
  FormGroup,
  Badge,
} from "reactstrap";
import {
  getEmailSettingsByMerchant,
  createEmailSettings,
  updateEmailSettings,
  toggleEmailSettingsActive,
  deleteEmailSettings,
} from "../../utils/apiCalls";
import { errorToast } from "../../shared/Toast";
import { useSelector } from "react-redux";
import ToggleSwitch from "../../shared/ToggleSwitch";

const initialForm = {
  smtpHost: "",
  smtpPort: 587,
  smtpSecure: false,
  smtpUser: "",
  smtpPassword: "",
  smtpFromName: "",
  fromEmail: "",
};

const EmailSettings = () => {
  const userType = localStorage.getItem("userType");
  const loggedInMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector(
    (state) => state.merchant || {}
  );
  const selectedMerchantId =
    userType === "merchant" ? loggedInMerchantId : reduxSelectedMerchant;

  const [emailSettings, setEmailSettings] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingSettings, setEditingSettings] = useState(null);
  const [formData, setFormData] = useState(initialForm);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchEmailSettings = async (merchantId) => {
    if (!merchantId) return;
    setIsFetching(true);
    const data = await getEmailSettingsByMerchant(merchantId);
    setEmailSettings(data || []);
    setIsFetching(false);
  };

  useEffect(() => {
    if (selectedMerchantId) {
      fetchEmailSettings(selectedMerchantId);
    } else {
      setEmailSettings([]);
    }
  }, [selectedMerchantId]);

  const resetForm = () => {
    setFormData(initialForm);
    setEditingSettings(null);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (settings) => {
    setEditingSettings(settings);
    setFormData({
      smtpHost: settings.smtpHost || "",
      smtpPort: settings.smtpPort || 587,
      smtpSecure: !!settings.smtpSecure,
      smtpUser: settings.smtpUser || "",
      smtpPassword: "",
      smtpFromName: settings.smtpFromName || "",
      fromEmail: settings.fromEmail || "",
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.smtpHost || !formData.smtpUser) {
      errorToast("SMTP Host and User are required");
      return;
    }
    if (!editingSettings && !formData.smtpPassword) {
      errorToast("SMTP Password is required");
      return;
    }

    const payload = {
      smtpHost: formData.smtpHost,
      smtpPort: Number(formData.smtpPort) || 587,
      smtpSecure: !!formData.smtpSecure,
      smtpUser: formData.smtpUser,
      smtpFromName: formData.smtpFromName || null,
      fromEmail: formData.fromEmail || null,
    };
    if (formData.smtpPassword) {
      payload.smtpPassword = formData.smtpPassword;
    }

    if (editingSettings) {
      const result = await updateEmailSettings(editingSettings.id, payload);
      if (result) {
        setModalOpen(false);
        fetchEmailSettings(selectedMerchantId);
      }
    } else {
      const result = await createEmailSettings({
        ...payload,
        MerchantId: Number(selectedMerchantId),
      });
      if (result) {
        setModalOpen(false);
        fetchEmailSettings(selectedMerchantId);
      }
    }
  };

  const handleToggleActive = async (id) => {
    const result = await toggleEmailSettingsActive(id);
    if (result) {
      fetchEmailSettings(selectedMerchantId);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const result = await deleteEmailSettings(deleteTarget.id);
    if (result) {
      setDeleteModal(false);
      setDeleteTarget(null);
      fetchEmailSettings(selectedMerchantId);
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="Email Settings" parent="Merchants" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">Email (SMTP) Configurations</h5>
                  {selectedMerchantId && (
                    <Button color="primary" onClick={openCreateModal}>
                      Add Email Settings
                    </Button>
                  )}
                </div>

                {!selectedMerchantId ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <p>Please select a merchant from the header to view email settings.</p>
                  </div>
                ) : isFetching ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <p>Loading email settings...</p>
                  </div>
                ) : (
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Host</th>
                        <th>Port</th>
                        <th>User</th>
                        <th>From</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emailSettings.length > 0 ? (
                        emailSettings.map((settings) => (
                          <tr key={settings.id} style={{ verticalAlign: "middle" }}>
                            <td>{settings.id}</td>
                            <td>{settings.smtpHost}</td>
                            <td>{settings.smtpPort}</td>
                            <td>{settings.smtpUser}</td>
                            <td>
                              {settings.smtpFromName
                                ? `${settings.smtpFromName} <${settings.fromEmail || settings.smtpUser}>`
                                : settings.fromEmail || settings.smtpUser}
                            </td>
                            <td>
                              <Badge color={settings.is_active ? "success" : "secondary"}>
                                {settings.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </td>
                            <td>
                              <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
                                <i
                                  className="fa fa-pencil"
                                  style={{ fontSize: 18, cursor: "pointer", color: "rgb(40, 167, 69)" }}
                                  title="Edit"
                                  onClick={() => openEditModal(settings)}
                                />
                                <ToggleSwitch
                                  checked={settings.is_active}
                                  handleToggle={() => handleToggleActive(settings.id)}
                                />
                                <i
                                  className="fa fa-trash"
                                  style={{ fontSize: 18, cursor: "pointer", color: "rgb(220, 53, 69)" }}
                                  title="Delete"
                                  onClick={() => {
                                    setDeleteTarget(settings);
                                    setDeleteModal(true);
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No email settings configured for this merchant.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Create / Edit Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setModalOpen(false)}>
          {editingSettings ? "Edit Email Settings" : "Add Email Settings"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md="8">
              <FormGroup>
                <Label>SMTP Host *</Label>
                <Input
                  type="text"
                  placeholder="smtp.gmail.com"
                  value={formData.smtpHost}
                  onChange={(e) => setFormData({ ...formData, smtpHost: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label>Port *</Label>
                <Input
                  type="number"
                  value={formData.smtpPort}
                  onChange={(e) => setFormData({ ...formData, smtpPort: e.target.value })}
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup check className="mb-3">
            <Label check>
              <Input
                type="checkbox"
                checked={formData.smtpSecure}
                onChange={(e) => setFormData({ ...formData, smtpSecure: e.target.checked })}
              />{" "}
              Use TLS/SSL (typically port 465 → checked, port 587 → unchecked)
            </Label>
          </FormGroup>
          <FormGroup>
            <Label>SMTP User (Email) *</Label>
            <Input
              type="email"
              placeholder="noreply@yourdomain.com"
              value={formData.smtpUser}
              onChange={(e) => setFormData({ ...formData, smtpUser: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <Label>
              SMTP Password {editingSettings && "(leave blank to keep current)"}
            </Label>
            <Input
              type="password"
              value={formData.smtpPassword}
              onChange={(e) => setFormData({ ...formData, smtpPassword: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <Label>From Name</Label>
            <Input
              type="text"
              placeholder="Okawa"
              value={formData.smtpFromName}
              onChange={(e) => setFormData({ ...formData, smtpFromName: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <Label>From Email (optional, defaults to SMTP User)</Label>
            <Input
              type="email"
              placeholder="hello@yourdomain.com"
              value={formData.fromEmail}
              onChange={(e) => setFormData({ ...formData, fromEmail: e.target.value })}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSave}>
            {editingSettings ? "Update" : "Create"}
          </Button>
          <Button color="secondary" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <ModalHeader toggle={() => setDeleteModal(false)}>Confirm Delete</ModalHeader>
        <ModalBody>
          Are you sure you want to delete email settings for{" "}
          <strong>{deleteTarget?.smtpUser}</strong> (ID: {deleteTarget?.id})?
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
          <Button color="secondary" onClick={() => setDeleteModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default EmailSettings;
